import { useEffect, useState } from "react";
import { Database, LockKey, MagnifyingGlass } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Usage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/usage")
      .then((r) => setData(r.data))
      .catch((e) => { if (e.response?.status === 403) setDenied(true); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="grain min-h-[50vh] flex items-center justify-center font-mono text-sm text-muted-foreground">loading<span className="cursor-blink">_</span></div>;

  if (denied) {
    return (
      <div className="grain mx-auto max-w-xl px-4 py-20 text-center">
        <LockKey size={44} className="text-primary mx-auto mb-4" />
        <h1 className="font-heading font-black text-white text-2xl">Upgrade required</h1>
        <p className="text-sm text-muted-foreground mt-3">
          The AI Usage Database is available only on the <span className="text-primary font-semibold">Unlimited (no-expiry)</span> plan.
          Your current plan doesn't include it.
        </p>
        <p className="text-sm text-muted-foreground mt-2">To upgrade, please contact <a href="mailto:info@metallization.in" className="text-secondary hover:underline">info@metallization.in</a></p>
      </div>
    );
  }

  const logs = (data?.logs || []).filter((l) =>
    !q || l.email.toLowerCase().includes(q.toLowerCase()) || (l.query || "").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="grain mx-auto max-w-[1400px] px-4 sm:px-6 py-8">
      <div className="flex items-center gap-3 mb-6">
        <Database size={26} weight="bold" className="text-primary" />
        <div>
          <h1 className="font-heading font-black text-white text-2xl">AI Usage Database</h1>
          <p className="text-sm text-muted-foreground">{data?.total || 0} recent searches · {data?.per_user?.length || 0} users active.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="border border-border">
          <div className="px-4 py-2.5 border-b border-border bg-muted font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Searches per user</div>
          <table className="w-full text-sm">
            <tbody>
              {(data?.per_user || []).map((u) => (
                <tr key={u.email} data-testid={`usage-user-${u.email}`} className="border-b border-border/60">
                  <td className="px-4 py-2.5 font-mono text-white">{u.email}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-primary">{u.count}</td>
                </tr>
              ))}
              {(!data?.per_user || data.per_user.length === 0) && <tr><td className="px-4 py-4 text-muted-foreground">No usage yet.</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="border border-border xl:col-span-2">
          <div className="px-4 py-2.5 border-b border-border bg-muted flex items-center justify-between gap-3">
            <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Recent searches</span>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input data-testid="usage-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="filter by user or query" className="pl-7 pr-2 py-1 text-xs font-mono bg-background border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-primary text-white" />
            </div>
          </div>
          <div className="overflow-x-auto max-h-[65vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-card">
                <tr className="border-b border-border">
                  {["User", "Type", "Query", "When"].map((h) => <th key={h} className="text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground px-3 py-2">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} className="border-b border-border/60 hover:bg-muted/30">
                    <td className="px-3 py-2 font-mono text-[11px] text-secondary">{l.email}</td>
                    <td className="px-3 py-2"><span className="border border-border bg-muted px-1.5 py-0.5 text-[10px] font-mono uppercase rounded-sm text-muted-foreground">{l.type}</span></td>
                    <td className="px-3 py-2 text-white text-xs max-w-[360px] truncate">{l.query}</td>
                    <td className="px-3 py-2 font-mono text-[10px] text-muted-foreground whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
